import React, { memo, useEffect, useState } from "react";
import { uploadFileToS3 } from "../../helper/utils";
import { useFetch } from "../../hooks/useFetch";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import DynamicButton from "../../component/button";
import BottomSheet from "./sheet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AToggleLoding } from "../../store/reducer/settings";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";
import { toast } from "react-toastify";

// ✅ Set the local worker
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

function BankStatement() {
  const router = useNavigate();
  const dispatch = useDispatch();
  const [query] = useSearchParams();
  const [loader, setLoader] = useState<boolean>(false);
  const state = useSelector((state: RootState) => state.invitation.invitation);
  const [open, setOpen] = useState<boolean>(false);
  const [token, setToken] = useState<string>("");
  const [BankStatement, setBankStatement] = useState<any[]>([]);
  const [mounted, setMounted] = useState<boolean>(false);
  const [Tokenresponse, Setresponse] = useState<any>({});

  useEffect(() => {
    if (!mounted) setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (mounted) {
      const token = query.get("token");
      if (token) setToken(token);
      else router("/invalid");
    }
  }, [mounted]);

  useEffect(() => {
    if (token) {
      dispatch(AToggleLoding());
      useFetch
        .post("b2c/fetch-token", { token })
        .then((res) => {
          const { data } = res;
          Setresponse(data.data || {});
        })
        .catch(() => router("/invalid"))
        .finally(() => dispatch(AToggleLoding()));
    }
  }, [token]);

  const uploadImage = async (filee: any, index: number) => {
    if (!filee) return;

    const { type: fileType, name: fileName } = filee;
    const fileExtension = fileName.split(".").pop()?.toLowerCase();

    // ✅ Check for duplicate file name
    const isDuplicate = BankStatement.some(
      (statement, idx) => statement?.fileName === fileName && idx !== index
    );
    if (isDuplicate) {
      toast.error("You have already uploaded this file");
      return;
    }

    // ✅ Validate PDF (check if readable, not password-protected)
    if (fileExtension === "pdf") {
      const fileBuffer = await filee.arrayBuffer();
      try {
        await pdfjsLib.getDocument({ data: fileBuffer }).promise;
      } catch (err: any) {
        if (err?.name === "PasswordException") {
          toast.error("Encrypted or password-protected files are not allowed");
        } else {
          toast.error("Failed to read PDF file.");
          console.error("Error reading PDF:", err);
        }
        return;
      }
    }

    dispatch(AToggleLoding());

    try {
      const { data }: any = await useFetch.get("medias/b2c-signed-url", {
        params: {
          requestType: "upload",
          b2cReqId: Tokenresponse.b2cReqId,
          cnic: Tokenresponse.cnic,
          scope: "any",
          fileName: `${Tokenresponse.cnic}_BankStatement_${index + 1}.pdf`,
          fileType,
          folderName: "b2c",
          key: state?.key,
        },
      });

      const url = data?.data?.signedUrl;
      await uploadFileToS3(url, filee);
      const urlPath = data?.data?.urlPath;

      setBankStatement((prev) => {
        const updated = [...prev];
        updated[index] = { urlPath, fileName }; // ✅ Include fileName for duplication check
        return updated;
      });

      setOpen(true);
    } catch (err) {
      toast.error("Upload failed. Please try again.");
    } finally {
      dispatch(AToggleLoding());
    }
  };

  const handleNext = () => {
    setLoader(true);

    const body = {
      id: Tokenresponse.b2cReqId,
      info: {
        bankStatement: BankStatement,
      },
    };

    return useFetch
      .post(`b2c/request/step`, body)
      .then((res: any) => {
        setLoader(false);
        router("?status=complete&step=3");
        return res?.data?.data?.id;
      })
      .catch((err) => {
        setLoader(false);
        console.log("ERROR", err?.response?.data);
      });
  };

  const ondelete = (index: number) => {
    setBankStatement((prev) => {
      const updated = [...prev];
      updated[index] = null;
      return updated;
    });
  };

  return (
    <>
      <div className="flex flex-col p-5 pt-10 justify-between h-[100vh] w-full overflow-x-hidden">
        <div>
          <div className="flex flex-col w-full items-center justify-center">
            <img src="/assets/uploadIcon.svg" alt="upload" />
            <h1 className="text-white text-center font-bold text-xl mt-3">
              Upload Proof
            </h1>
            <p className="text-center text-white text-[15px] mt-4">
              To verify your bank details, upload bank statements from the bank
              account you provided. Ensure the statement covers the last{" "}
              <b>3 recent months</b>, or your request will be rejected.
            </p>
          </div>

          {[0, 1, 2].map((index) => (
            <div key={index} className="mt-6">
              <h2 className="text-white text-[13px]">
                Bank Statement {index + 1} *
              </h2>
              <input
                type="file"
                className="hidden"
                id={`bankfile${index + 1}`}
                accept=".pdf"
                onChange={(e: any) => uploadImage(e.target.files[0], index)}
              />
              <div className="relative w-[70px]">
                {BankStatement[index]?.urlPath && (
                  <button
                    onClick={() => ondelete(index)}
                    className="h-5 w-5 absolute bg-white rounded-xl flex items-center justify-center -top-2 -right-2"
                  >
                    <h1>X</h1>
                  </button>
                )}
                <label htmlFor={`bankfile${index + 1}`}>
                  <div className="flex mt-4 p-2 h-[70px] w-[70px] rounded-[5px] bg-[#172A46] items-center justify-center overflow-hidden">
                    {BankStatement[index]?.urlPath ? (
                      <img
                        src={"./assets/pdfIcon.png"}
                        className="w-full h-full"
                        alt="pdf"
                      />
                    ) : (
                      <h1 className="text-white text-[30px]">+</h1>
                    )}
                  </div>
                </label>
                <h2 className="text-[#C8D3F5] opacity-[0.5] text-[12px] font-bold mt-2 ml-1s">
                  Upload File
                </h2>
              </div>
            </div>
          ))}
        </div>

        <div className="flex mt-10 w-full items-start justify-center">
          <DynamicButton
            isDisabled={BankStatement.filter((f) => f?.urlPath).length < 3}
            type="submit"
            isLoading={loader}
            onClick={handleNext}
          >
            CONFIRM
          </DynamicButton>
        </div>
      </div>
    </>
  );
}

export default memo(BankStatement);
